// Workforce › Identity state (WS-C): portal accounts (consumer profiles),
// invites, agents, identities and actors as seen from one portal.
// Exported through ../index.ts already; add exports without touching that file.
import type {
  DashboardInstancePortalsConsumerInvitesCreateBody,
  DashboardInstancePortalsConsumerInvitesListQuery,
  DashboardInstancePortalsConsumerProfilesListQuery,
  DashboardInstanceSessionsConnectionsListQuery
} from '@metorial/dashboard-sdk';
import { createLoader } from '@metorial/data-hooks';
import { consumerLoader, consumersLoader } from '../consumer/loaders/consumers';
import { portalConsumerProfilesLoader } from '../consumer/loaders/portalConsumers';
import { identityActorsLoader } from '../identity/loaders/identityActors';
import { identitiesLoader } from '../identity/loaders/identities';
import { usePaginator } from '../lib/usePaginator';
import { withAuth } from '../user';

type PortalInput = { instanceId: string; portalId: string };

let portalKey = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  ...rest: any[]
) => (instanceId && portalId ? JSON.stringify([instanceId, portalId, ...rest]) : null);

// ---------------------------------------------------------------------------
// Accounts (= portal consumer profiles)

export let portalAccountsLoader = createLoader({
  name: 'workforcePortalAccounts',
  parents: [portalConsumerProfilesLoader],
  fetch: (i: PortalInput & DashboardInstancePortalsConsumerProfilesListQuery) =>
    withAuth(sdk => sdk.portals.consumerProfiles.list(i.instanceId, i.portalId, i)),
  mutators: {}
});

export let usePortalAccounts = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  query?: DashboardInstancePortalsConsumerProfilesListQuery | null
) =>
  usePaginator(
    pagination =>
      portalAccountsLoader.use(
        instanceId && portalId && query !== null
          ? { instanceId, portalId, ...pagination, ...(query ?? {}) }
          : null
      ),
    portalKey(instanceId, portalId, 'accounts', query ?? {})
  );

export let portalAccountLoader = createLoader({
  name: 'workforcePortalAccount',
  parents: [portalAccountsLoader, portalConsumerProfilesLoader],
  fetch: (i: PortalInput & { accountId: string }) =>
    withAuth(sdk => sdk.portals.consumerProfiles.get(i.instanceId, i.portalId, i.accountId)),
  mutators: {
    assignGroups: (
      body: { groupIds: string[] },
      { input }: { input: PortalInput & { accountId: string } }
    ) =>
      withAuth(sdk =>
        sdk.portals.consumerProfiles.assignGroups(
          input.instanceId,
          input.portalId,
          input.accountId,
          body
        )
      ),

    unassignGroups: (
      body: { groupIds: string[] },
      { input }: { input: PortalInput & { accountId: string } }
    ) =>
      withAuth(sdk =>
        sdk.portals.consumerProfiles.unassignGroups(
          input.instanceId,
          input.portalId,
          input.accountId,
          body
        )
      )
  }
});

export let usePortalAccount = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  accountId: string | null | undefined
) => {
  let data = portalAccountLoader.use(
    instanceId && portalId && accountId ? { instanceId, portalId, accountId } : null
  );

  return {
    ...data,
    useAssignGroupsMutator: data.useMutator('assignGroups'),
    useUnassignGroupsMutator: data.useMutator('unassignGroups')
  };
};

/**
 * Account Settings › Save. A portal account's name and email live on its
 * consumer; the API copies them onto every profile of that consumer.
 */
export let useUpdatePortalAccountDetails = portalAccountLoader.createExternalMutator(
  (i: { instanceId: string; consumerId: string; name?: string; email?: string }) =>
    withAuth(async sdk => {
      let consumer = await sdk.consumers.update(i.instanceId, i.consumerId, {
        name: i.name,
        email: i.email
      });

      consumersLoader.refetchAll();
      consumerLoader.refetchAll();
      identityActorsLoader.refetchAll();
      identitiesLoader.refetchAll();

      return consumer;
    })
);

// ---------------------------------------------------------------------------
// Invites

export let portalInvitesLoader = createLoader({
  name: 'workforcePortalInvites',
  parents: [portalAccountsLoader, portalConsumerProfilesLoader],
  fetch: (i: PortalInput & DashboardInstancePortalsConsumerInvitesListQuery) =>
    withAuth(sdk => sdk.portals.consumerInvites.list(i.instanceId, i.portalId, i)),
  mutators: {}
});

export let usePortalInvites = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  query?: DashboardInstancePortalsConsumerInvitesListQuery | null
) =>
  usePaginator(
    pagination =>
      portalInvitesLoader.use(
        instanceId && portalId && query !== null
          ? { instanceId, portalId, ...pagination, ...(query ?? {}) }
          : null
      ),
    portalKey(instanceId, portalId, 'invites', query ?? {})
  );

export let useCreatePortalInvite = portalInvitesLoader.createExternalMutator(
  (i: PortalInput & DashboardInstancePortalsConsumerInvitesCreateBody) =>
    withAuth(sdk =>
      sdk.portals.consumerInvites.create(i.instanceId, i.portalId, {
        name: i.name,
        email: i.email,
        message: i.message
      })
    ),
  { disableToast: true }
);

/**
 * The link the invite email would contain (same format as the API's
 * `buildConsumerInviteUrl`). Shown in the dashboard because local installs
 * usually have no SMTP, so the email may never arrive.
 */
export let buildPortalInviteUrl = (invite: {
  id: string;
  portalUrl: string | null;
  consumerProfile: { id: string; email: string };
}) => {
  if (!invite.portalUrl) return null;

  try {
    let url = new URL(invite.portalUrl);
    url.searchParams.set('consumer_invite_id', invite.id);
    url.searchParams.set('consumer_profile_id', invite.consumerProfile.id);
    url.searchParams.set('email', invite.consumerProfile.email);
    return url.toString();
  } catch {
    return invite.portalUrl;
  }
};

// ---------------------------------------------------------------------------
// MCP connections made by one consumer (account › Connections)

type ConsumerConnectionsQuery = Omit<
  DashboardInstanceSessionsConnectionsListQuery,
  'consumerId'
>;

export let consumerSessionConnectionsLoader = createLoader({
  name: 'workforceConsumerSessionConnections',
  parents: [],
  fetch: (i: { instanceId: string; consumerId: string } & ConsumerConnectionsQuery) =>
    withAuth(sdk => sdk.sessions.connections.list(i.instanceId, i)),
  mutators: {}
});

export let useConsumerSessionConnections = (
  instanceId: string | null | undefined,
  consumerId: string | null | undefined,
  query?: ConsumerConnectionsQuery
) =>
  usePaginator(
    pagination =>
      consumerSessionConnectionsLoader.use(
        instanceId && consumerId ? { instanceId, consumerId, ...pagination, ...query } : null
      ),
    instanceId && consumerId ? JSON.stringify([instanceId, consumerId, query ?? {}]) : null
  );
